import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import maxBy from 'lodash/maxBy';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import clsx from 'clsx';
import { selectAverageData, selectChartData, selectTextListData, selectDifferenceData } from '../../../../utils';
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import SRLStudentResponses from '../SRLStudentResponses/SRLStudentResponses';
import SRLDifferenceIndicator from '../SRLDifferenceIndicator/SRLDifferenceIndicator';
import SRLKeyNumber from '../SRLKeyNumber/SRLKeyNumber';
import styles from './SRLConfidenceContent.module.css';

const MINIMUM_VALUE = '1';
const MAXIMUM_VALUE = '5';
const X_OFFSET = 20;

const CustomTick = ({ x, y, payload }) => {
  if ([MINIMUM_VALUE, MAXIMUM_VALUE].includes(payload.value)) {
    let value;
    let xTransform;
    if (payload.value === MINIMUM_VALUE) {
      xTransform = x + X_OFFSET;
      value = 'not confident';
    } else {
      xTransform = x - X_OFFSET;
      value = 'very confident';
    }

    return (
      <text x={xTransform} y={y} textAnchor="middle" fontSize="12px" fontStyle="italic" fill="var(--prs-c-gray-950)">
        {value}
      </text>
    );
  }
  return null;
};

CustomTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.object,
};

const getConfidenceChart = (chartData, height) => {
  let highDomainLimit = 40;
  const maxData = maxBy(chartData, 'student_count');
  if (maxData) {
    highDomainLimit = Number(maxData.student_count);
  }

  if (Array.isArray(chartData)) {
      // Pull out text after 1-5 so we just get number values
    chartData.forEach((barData) => { barData.answer = barData.answer.charAt(0); });
  }

  return (
    <ResponsiveContainer height={height}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 0, left: -20, bottom: 0 }}
      >
        <Bar
          dataKey="student_count"
          fill="var(--prs-c-info-800)"
          minPointSize={0}
          label={{
            value: 'student_count',
            position: 'top',
            fontFamily: 'var(--prs-ff-sans)',
            fontSize: 14,
            fill: 'var(--prs-c-gray-950)' }}
        />
        <XAxis
          dataKey="answer"
          tickLine={false}
          tick={{
            fill: 'var(--prs-c-gray-700)',
            fontSize: 12,
          }}
        />
        <XAxis
          dataKey="answer"
          tick={CustomTick}
          tickLine={false}
          axisLine={false}
          interval={0}
          xAxisId="endIntervals"
        />
        <YAxis
          domain={[0, highDomainLimit]}
          label={{ value: 'total students',
            angle: -90,
            position: 'center',
            fontSize: 14,
            fontFamily: 'var(--prs-ff-sans)',
            fontStyle: 'italic',
            fill: 'var(--prs-c-gray-700)',
          }}
          tick={false}
          axisLine={false}
          tickLine={false}
        />
        <CartesianGrid vertical={false} horizontal={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};


const renderStudentResponses = (prompt, studentResponses) => (
  <SRLStudentResponses
    prompt={prompt}
    studentResponses={studentResponses}
    responseLimit={2}
    lineLimit={4}
  />
);

const renderAverage = (averageData, differenceData) => {
  const containerClasses = clsx({
    [styles.containerPadding]: !!differenceData,
  });
  return (
    <div className={containerClasses}>
      <SRLKeyNumber
        keyNumber={round(averageData.average, 1)}
        postScript={`/${averageData.max_value}`}
        label="class average"
      />
      {differenceData &&
        <SRLDifferenceIndicator
          lastAssignmentValue={differenceData.last_assignment}
          thisAssignmentValue={differenceData.this_assignment}
          unitOfMeasurement=""
          numberOfDecimals={1}
          containerClasses={styles.containerPadding}
        />
      }
    </div>
  );
};

const getAverageAndChart = (averageData, chartData) => (
  <div>
    {renderAverage(averageData)}
    {getConfidenceChart(chartData, 180)}
  </div>
);

function SRLConfidenceContent({ data, isMobileXS }) {
  const { insightsData, isSRLIntroSurvey } = data;

  const averageData = selectAverageData(insightsData);
  const chartData = selectChartData(insightsData);

  let rightColumnContent = null;
  let leftColumnContent = null;
  if (isSRLIntroSurvey) {
    const { prompt, studentResponses } = selectTextListData(insightsData);
    if (averageData.average > 0) {
      leftColumnContent = getAverageAndChart(averageData, chartData);
    }
    if (studentResponses.length > 0) {
      rightColumnContent = renderStudentResponses(prompt, studentResponses);
    }
  } else {
    const differenceData = selectDifferenceData(insightsData);
    if (averageData.average > 0) {
      leftColumnContent = renderAverage(averageData, differenceData);
    }
    if (!isEmpty(chartData)) {
      rightColumnContent = getConfidenceChart(chartData, 240);
    }
  }

  return (
    <SplitContentContainer
      leftColumnContent={leftColumnContent}
      rightColumnContent={rightColumnContent}
      borderPosition={isSRLIntroSurvey ? 'center' : 'left'}
      stackedVersion={isMobileXS}
    />
  );
}

SRLConfidenceContent.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool,
};

export default SRLConfidenceContent;
